<template>
  <div>
    <ExercisesHeader :image="routineImage">
      <template #boxDesktop>
        <h1 class="fluid-3xl leading-none break-words hyphens-auto">{{ entry?.title }}</h1>
        <div class="my-4 fluid-lg font-light leading-normal" v-html="entry?.teaserText"></div>
        <div class="md:flex-row flex flex-col mt-6 space-x-8">
          <RoutineFeatures
            :duration="routineDuration"
            :exercise-count="routineExerciseCount"
            :difficulty="routineDifficulty"
            class="w-full"
          >
            <RoutinePlayFull :asset="routineVideo" :routine="entry" class="h-12 mt-4" />
          </RoutineFeatures>
        </div>
      </template>
      <template #boxMobile>
        <h1 class="fluid-4xl leading-none">{{ entry?.title }}</h1>
        <div class="my-4 fluid-lg font-light leading-normal" v-html="entry?.teaserText"></div>
        <RoutineFeatures
          :duration="routineDuration"
          :exercise-count="routineExerciseCount"
          :difficulty="routineDifficulty"
          class="w-full mt-4"
        >
          <RoutinePlayFull :asset="routineVideo" :routine="entry" class="h-12 mt-4" />
        </RoutineFeatures>
      </template>
      <template #imageOverlay>
        <div class="container relative">
          <div
            v-if="accountEnabled"
            class="absolute bottom-0 right-0 flex flex-row items-center p-2 pl-6 m-4 bg-gray-900/50 rounded-full"
          >
            <span class="fluid-xs leading-tightest text-white mr-2">{{ $t('exercises.addRoutine') }}</span>
            <LikeBtn :handle="entry.id" :entry="entry" :exercise="entry" class="inline-block ml-4" type="routine" />
          </div>
        </div>
      </template>
    </ExercisesHeader>
    <RoutineProductsBar :products="routineEntry?.products" />
    <div v-if="entry?.description" class="bg-putty">
      <div class="md:flex-row md:py-24 container flex flex-col max-w-screen-xl py-12">
        <div
          class="md:w-7/12 md:pr-24 text-copy fluid-base leading-relaxed font-light w-full"
          v-html="entry.description"
        ></div>
        <div
          class="md:w-5/12 md:pl-24 md:border-l text-copy fluid-base leading-relaxed font-light w-full border-gray-400"
          v-html="entry.instructions"
        ></div>
      </div>
    </div>
    <ContentBlocks :entry="entry" />
  </div>
</template>
<script>
import gql from 'graphql-tag';
import Routine from '@/mixins/Routine';
import ExercisesHeader from '@/components/exercises/ExercisesHeader';
import RoutineProductsBar from '@/components/routine/RoutineProductsBar';
import RoutineFeatures from '@/components/exercises/RoutineFeatures';
import RoutinePlayFull from '@/components/routine/RoutinePlayFull';
import LikeBtn from '@/components/common/LikeBtn';
import ContentBlocks from '@/components/common/ContentBlocks';
import { ContentBlocksMatrixFragment } from '@/components/common/ContentBlocksMatrixFragment.js';

const RoutineFragment = gql`
  fragment RoutineFragment on routine_Entry {
    typeHandle
    id
    slug
    uri
    title
    seoTopics
    image {
      kind
      url
    }
    video {
      ...CraftAssetFragment
    }
    duration
    teaserText
    description
    instructions
    products {
      ... on products_Entry {
        uri
        title
        icon {
          url
        }
      }
    }
    sports {
      title
    }
    painPoints {
      title
    }
    trainingGoals {
      title
    }
    difficulty {
      title
    }
    exerciseCount
    contentBlocks {
      ...ContentBlocksMatrixFragment
    }
  }
  ${ContentBlocksMatrixFragment}
`;
const WorkoutCollectionFragment = gql`
  fragment WorkoutCollectionFragment on workoutCollection_Entry {
    typeHandle
    id
    slug
    uri
    title
    seoTopics
    image {
      kind
      url
    }
    video {
      ...CraftAssetFragment
    }
    duration
    teaserText
    description
    instructions
    products {
      ... on products_Entry {
        uri
        title
        icon {
          url
        }
      }
    }
    sports {
      title
    }
    painPoints {
      title
    }
    trainingGoals {
      title
    }
    difficulty {
      title
    }
    exerciseCount
    pimWorkout
    contentBlocks {
      ...ContentBlocksMatrixFragment
    }
  }
  ${ContentBlocksMatrixFragment}
`;

const WorkoutFragment = gql`
  fragment WorkoutFragment on workout_Entry {
    typeHandle
    id
    slug
    uri
    title
    seoTopics
    image {
      kind
      url
    }
    video {
      ...CraftAssetFragment
    }
    duration
    teaserText
    description
    instructions
    products {
      ... on products_Entry {
        uri
        title
        icon {
          url
        }
      }
    }
    sports {
      title
    }
    painPoints {
      title
    }
    trainingGoals {
      title
    }
    difficulty {
      title
    }
    exerciseCount
    contentBlocks {
      ...ContentBlocksMatrixFragment
    }
  }
  ${ContentBlocksMatrixFragment}
`;

export const RoutinePageFragment = gql`
  fragment RoutinePageFragment on EntryInterface {
    ...RoutineFragment
    ...WorkoutFragment
    ...WorkoutCollectionFragment
  }
  ${RoutineFragment}
  ${WorkoutFragment}
  ${WorkoutCollectionFragment}
`;

/**
 * Renders transparent Routine or Workout
 */
export default defineNuxtComponent({
  name: 'RoutinePage',
  components: {
    ContentBlocks,
    ExercisesHeader,
    RoutineProductsBar,
    RoutineFeatures,
    RoutinePlayFull,
    LikeBtn,
  },
  mixins: [Routine],
  computed: {
    accountEnabled() {
      return this.$config.public.auth.isEnabled;
    },
  },
});
</script>
